import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {formatMinor} from '@/entities/salary/money';
import type {PayrollRow} from '@/entities/salary/types';
import {PersonAvatar} from '@/entities/personnel/ui/PersonAvatar';
import {formatHoursFromMinutes, formatIsoDate} from '@/shared/lib/format';
import {PressCard} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Cell, NameCell, type Column} from '@/shared/ui/table';
import {DataList} from '@/shared/ui/data-list';
import type {SortAccessors} from '@/shared/lib/table-sort';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';
import {flagLabelKey, primaryFlag} from '../lib/flags';

/**
 * Строки ведомости: кто, сколько дней явки из плановых, и сколько начислено.
 *
 * ## Пустая сумма — это надпись, а не прочерк
 *
 * Строка без начисленного несёт флаг, и флаг печатается прямо в колонке суммы. Прочерк
 * сказал бы «ноль» или «неизвестно»; «оклад не задан» говорит, что именно сделать и
 * где — а сделать это можно в один тап, потому что строка открывает карточку человека.
 *
 * ## Почему дни явки и плановые дни стоят рядом
 *
 * Начисленное по окладу — это `ставка × дней явки ÷ плановых дней МЕСЯЦА`. Вопрос
 * «почему у него столько» задают на второй день, и ответ на него — эта дробь. Экран,
 * который печатает только сумму, отправляет спрашивающего к калькулятору.
 *
 * Плановые дни здесь — дни ПЕРИОДА, а знаменатель у сервера месячный; поэтому колонка
 * подписана «явка», а не «доля оклада», и не притворяется, что показывает саму дробь.
 */
/** Строк в странице ведомости, и что предлагает рейка под ней. */
/** Порядок по умолчанию и то, чем его меняют на телефоне. Ссылки модульные:
    новая на каждый рендер пересортировала бы ведомость впустую. */
const INITIAL_SORT = {key: 'name', direction: 'asc'} as const;
const PHONE_SORTS = ['name', 'present', 'worked', 'accrued'] as const;
const PHONE_SORT_LABELS: Record<string, string> = {
  name: 'reports.col.name',
  present: 'payroll.colPresent',
  worked: 'payroll.colWorked',
  accrued: 'payroll.colAccrued',
};

/**
 * Чем сортируется каждый столбец.
 *
 * `accrued_minor: null` — это НЕ ноль, а «оклад не задан»: общее правило пустого
 * (см. shared/lib/table-sort) уносит такие строки вниз в обе стороны, потому что,
 * перевернув столбец, бухгалтер ждёт самых маленьких начислений, а не тех, у кого их
 * нет вовсе.
 */
const ACCESSORS: SortAccessors<PayrollRow> = {
  name: row => row.full_name,
  number: row => row.employee_number,
  department: row => row.department_names[0] ?? null,
  present: row => row.present_days,
  worked: row => row.worked_minutes,
  accrued: row => row.accrued_minor,
};

const styles = StyleSheet.create({
  frame: {gap: spacing(3)},
  pager: {paddingHorizontal: spacing(1)},
  list: {gap: spacing(2)},
  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  body: {flex: 1, gap: 2},
  amount: {alignItems: 'flex-end', gap: 2, maxWidth: '55%'},
  amountText: {textAlign: 'right'},
});

/**
 * Сумма — или причина, по которой её нет.
 *
 * `withCurrency` разводит два места, где эта ячейка живёт. В карточке на телефоне
 * колонок нет вовсе, и валюта обязана стоять при сумме — иначе в компании с двумя
 * валютами два числа читаются как сравнимые. В таблице на десктопе валюта уже занимает
 * собственную колонку, и повторять её под каждым числом значит печатать «UZS» дважды в
 * одной строке.
 */
function AccruedCell({
  row,
  muted,
  withCurrency,
}: {
  row: PayrollRow;
  muted: string;
  withCurrency: boolean;
}) {
  const {t} = useTranslation();
  const flag = primaryFlag(row.flags);

  if (row.accrued_minor == null) {
    return (
      <StatusPill
        tone="pending"
        size="sm"
        dot={false}
        label={t(flagLabelKey(flag ?? ''))}
      />
    );
  }

  return (
    <View style={styles.amount}>
      <AppText variant="mono" style={styles.amountText}>
        {formatMinor(row.accrued_minor)}
      </AppText>
      {withCurrency ? (
        <AppText variant="micro" color={muted} style={styles.amountText}>
          {row.currency}
        </AppText>
      ) : null}
    </View>
  );
}

function PayrollCard({
  row,
  companyId,
  onOpen,
}: {
  row: PayrollRow;
  companyId?: string;
  onOpen: () => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  return (
    <PressCard
      padding={3}
      onPress={onOpen}
      accessibilityLabel={row.full_name}
      testID={`payroll-row-${row.record_id}`}>
      <View style={styles.row}>
        {companyId ? (
          <PersonAvatar
            companyId={companyId}
            recordId={row.record_id}
            name={row.full_name}
            size={40}
          />
        ) : null}
        <View style={styles.body}>
          <AppText variant="subtitle" numberOfLines={1}>
            {row.full_name}
          </AppText>
          <AppText variant="caption" numberOfLines={1}>
            {t('payroll.attendanceLine', {
              present: row.present_days,
              scheduled: row.scheduled_days,
            })}
          </AppText>
          {formatIsoDate(row.detached_at) ? (
            <AppText variant="micro" numberOfLines={1} color={p.mutedForeground}>
              {t('reports.detachedOn', {date: formatIsoDate(row.detached_at)})}
            </AppText>
          ) : null}
        </View>
        <AccruedCell row={row} muted={p.mutedForeground} withCurrency />
      </View>
    </PressCard>
  );
}

export function PayrollList({
  rows,
  onOpen,
  companyId,
}: {
  rows: readonly PayrollRow[];
  /** Открыть карточку человека — оттуда правится и оклад, и всё остальное. */
  onOpen: (row: PayrollRow) => void;
  /** Чьи лица тянуть. Без него — инициалы: столько же строк, ноль запросов. */
  companyId?: string;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  /*
    Сортировка честная: ведомость приходит одним ответом за месяц, разбивка тоже
    клиентская, — значит «сначала кто больше заработал» означает по всей ведомости, а
    не по видимой странице.

    `accrued_minor: null` — это НЕ ноль, а «оклад не задан», и общее правило пустого
    (см. shared/lib/table-sort) уносит такие строки вниз в обе стороны: перевернув
    столбец, бухгалтер ждёт самых маленьких начислений, а не тех, у кого их нет.
  */
  const columns: Column<PayrollRow>[] = [
    {
      key: 'name',
      sortKey: 'name',
      title: t('reports.col.employee'),
      flex: 2.2,
      render: row => (
        <NameCell
          detail={
            formatIsoDate(row.detached_at)
              ? t('reports.detachedOn', {date: formatIsoDate(row.detached_at)})
              : undefined
          }
          mark={
            companyId ? (
              <PersonAvatar
                companyId={companyId}
                recordId={row.record_id}
                name={row.full_name}
                size={28}
              />
            ) : undefined
          }>
          {row.full_name}
        </NameCell>
      ),
    },
    {
      key: 'number',
      sortKey: 'number',
      title: t('reports.col.number'),
      flex: 1,
      minWidth: 1180,
      render: row => (
        <Cell tone={p.mutedForeground}>{row.employee_number || '—'}</Cell>
      ),
    },
    {
      key: 'department',
      sortKey: 'department',
      title: t('home.people.colDepartment'),
      flex: 1.4,
      minWidth: 1320,
      render: row => (
        <Cell tone={p.mutedForeground}>{row.department_names[0] ?? '—'}</Cell>
      ),
    },
    {
      key: 'present',
      sortKey: 'present',
      title: t('payroll.colPresent'),
      width: 110,
      align: 'right',
      render: row => <Cell>{`${row.present_days} / ${row.scheduled_days}`}</Cell>,
    },
    {
      key: 'worked',
      sortKey: 'worked',
      title: t('payroll.colWorked'),
      width: 120,
      align: 'right',
      minWidth: 1080,
      render: row => (
        <Cell tone={p.mutedForeground}>
          {formatHoursFromMinutes(
            row.worked_minutes,
            t('attendance.units.hour'),
            t('attendance.units.minute'),
          )}
        </Cell>
      ),
    },
    {
      key: 'accrued',
      sortKey: 'accrued',
      title: t('payroll.colAccrued'),
      width: 170,
      align: 'right',
      render: row => (
        <AccruedCell row={row} muted={p.mutedForeground} withCurrency={false} />
      ),
    },
    {
      key: 'currency',
      title: t('payroll.colCurrency'),
      width: 72,
      render: row => <Cell tone={p.mutedForeground}>{row.currency || '—'}</Cell>,
    },
  ];

  return (
    <DataList
      testID="payroll"
      rows={rows}
      columns={columns}
      keyOf={row => row.record_id}
      accessors={ACCESSORS}
      initialSort={INITIAL_SORT}
      onRowPress={onOpen}
      phoneSorts={PHONE_SORTS}
      sortLabel={key => t(PHONE_SORT_LABELS[key])}
      hint={{table: t('reports.tableHint'), cards: t('reports.cardsHint')}}
      empty={<EmptyState icon="people" title={t('payroll.empty')} />}
      noMatches={<EmptyState icon="people" title={t('payroll.empty')} />}
      renderCard={row => (
        <PayrollCard row={row} companyId={companyId} onOpen={() => onOpen(row)} />
      )}
    />
  );
}
